import React, { useState, useEffect } from 'react';
import { 
  Calendar, MessageSquare, Users, Activity, FileText, Bell, 
  Settings, Menu, X, ChevronRight, Send, Phone, Video, 
  Pill, Heart, Clock, AlertCircle, CheckCircle, LogOut
} from 'lucide-react';

// WASM declarations
declare global {
  interface Window {
    wasm?: any;
  }
}

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [token, setToken] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [wasmReady, setWasmReady] = useState(false);
  const [manager, setManager] = useState<any>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Data states
  const [patients, setPatients] = useState<any[]>([]);
  const [patientData, setPatientData] = useState<any>(null);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [selectedChat, setSelectedChat] = useState<any>(null);
  const [newMessage, setNewMessage] = useState('');

  const apiUrl = (import.meta as any).env.VITE_API_URL || '';

  // Auth Handling
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      const response = await fetch(`${apiUrl}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setToken(data.token);
        setUser(data.user);
        setIsLoggedIn(true);
        localStorage.setItem('fertilcare_token', data.token);
        localStorage.setItem('fertilcare_user', JSON.stringify(data.user));
      } else {
        setLoginError(data.error || 'Erro ao fazer login');
      }
    } catch (err) {
      setLoginError('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setToken(null);
    setUser(null);
    localStorage.removeItem('fertilcare_token');
    localStorage.removeItem('fertilcare_user');
    setCurrentView('dashboard');
  };

  // WASM Initialization
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.wasm && !wasmReady) {
        setWasmReady(true);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [wasmReady]);

  useEffect(() => {
    if (wasmReady && window.wasm) {
      const wasm = window.wasm;
      try {
        if (typeof wasm.FertilCareManager === 'function') {
          const newManager = new wasm.FertilCareManager();
          setManager(newManager);
          console.log('Rust FertilCareManager initialized');
        }
      } catch (err) {
        console.error('Error creating FertilCareManager:', err);
      }
    }
  }, [wasmReady]);

  // Data Fetching
  const fetchData = async () => {
    if (!token || !user) return;
    setLoading(true);
    try {
      if (user.role === 'doctor' || user.role === 'secretary') {
        const response = await fetch(`${apiUrl}/api/patients`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (Array.isArray(data)) {
          setPatients(data);
          if (manager) manager.setPatients(data);
        }
      } else if (user.role === 'patient') {
        const response = await fetch(`${apiUrl}/api/patient/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setPatientData(data);
        // For patient, we set a list with just themselves for the manager
        if (manager) manager.setPatients([data]);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedToken = localStorage.getItem('fertilcare_token');
    const savedUser = localStorage.getItem('fertilcare_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsLoggedIn(true);
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn, manager]);

  const markMedicationTaken = async (patientId: number, medId: number, medName: string, medDate: string) => {
    if (!token) return;
    try {
      const response = await fetch(`${apiUrl}/api/medications/${medId}/take`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        // Update WASM
        if (manager) {
          manager.markMedicationTaken(patientId, medName, medDate);
        }
        // Refresh local data
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Login View
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center text-white shadow-lg">
            <Heart className="w-10 h-10" />
          </div>
          <h2 className="mt-6 text-center text-4xl font-extrabold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            FertilCare
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 font-medium">
            Gestão Especializada em Reprodução Assistida
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-2xl sm:rounded-3xl sm:px-10 border border-purple-50">
            <form className="space-y-6" onSubmit={handleLogin}>
              <div>
                <label className="block text-sm font-semibold text-gray-700">Email</label>
                <div className="mt-1">
                  <input
                    type="email"
                    required
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700">Senha</label>
                <div className="mt-1">
                  <input
                    type="password"
                    required
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="appearance-none block w-full px-4 py-3 border border-gray-200 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {loginError && (
                <div className="bg-red-50 p-3 rounded-lg flex items-center gap-2 text-red-600 text-sm animate-shake">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
              >
                {loading ? 'Autenticando...' : 'Entrar na Plataforma'}
              </button>
            </form>
            <div className="mt-6 pt-6 border-t border-gray-100">
              <p className="text-center text-xs text-gray-400">
                Acesso restrito a profissionais e pacientes autorizados.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Navigation config
  const getNavigation = () => {
    switch(user?.role) {
      case 'patient':
        return [
          { id: 'dashboard', label: 'Início', icon: Activity },
          { id: 'cycle', label: 'Meu Ciclo', icon: Calendar },
          { id: 'medications', label: 'Medicações', icon: Pill },
          { id: 'appointments', label: 'Consultas', icon: Clock },
          { id: 'chat', label: 'Mensagens', icon: MessageSquare }
        ];
      case 'doctor':
        return [
          { id: 'dashboard', label: 'Painel', icon: Activity },
          { id: 'patients', label: 'Pacientes', icon: Users },
          { id: 'schedule', label: 'Agenda', icon: Calendar },
          { id: 'chat', label: 'Mensagens', icon: MessageSquare }
        ];
      case 'secretary':
        return [
          { id: 'dashboard', label: 'Recepção', icon: Activity },
          { id: 'schedule', label: 'Agenda', icon: Calendar },
          { id: 'patients', label: 'Pacientes', icon: Users }
        ];
      default:
        return [];
    }
  };

  // Sub-renders
  const renderPatientDashboard = () => {
    if (!patientData) return <div className="animate-pulse">Carregando dados...</div>;
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 rounded-3xl text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="text-3xl font-bold mb-2">Olá, {user.name}</h2>
            <p className="text-purple-100 font-medium">Status: {patientData.status} • {patientData.cycle}</p>
          </div>
          <Heart className="absolute right-[-20px] bottom-[-20px] w-48 h-48 text-white/10" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Próxima Consulta</p>
                <p className="text-lg font-bold text-gray-800">{patientData.next_appointment}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Fase Atual</p>
                <p className="text-lg font-bold text-gray-800">{patientData.cycleData?.phase || 'Preparação'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Pill className="w-5 h-5 text-purple-600" />
            Medicações para Hoje
          </h3>
          <div className="space-y-3">
            {patientData.medications?.map((med: any) => (
              <div key={med.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-transparent hover:border-purple-100 transition-all">
                <div>
                  <p className="font-bold text-gray-800">{med.name}</p>
                  <p className="text-sm text-gray-500">{med.dosage} • {med.time}</p>
                </div>
                {med.taken ? (
                  <div className="flex items-center gap-2 text-green-600 font-bold text-sm">
                    <CheckCircle className="w-5 h-5" /> Confirmada
                  </div>
                ) : (
                  <button 
                    onClick={() => markMedicationTaken(patientData.id, med.id, med.name, med.date)}
                    className="px-4 py-2 bg-white border border-purple-200 text-purple-600 rounded-xl text-xs font-bold hover:bg-purple-600 hover:text-white transition-all shadow-sm"
                  >
                    Marcar como tomada
                  </button>
                )}
              </div>
            ))}
            {(!patientData.medications || patientData.medications.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-4 italic">Nenhuma medicação agendada para hoje.</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDoctorDashboard = () => {
    const activeAlerts = patients.reduce((acc, p) => acc + (p.alerts?.length || 0), 0);
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Pacientes Ativos</p>
            <p className="text-4xl font-black text-gray-800">{patients.length}</p>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Consultas Hoje</p>
            <p className="text-4xl font-black text-purple-600">8</p>
          </div>
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Transferências</p>
            <p className="text-4xl font-black text-pink-600">3</p>
          </div>
          <div className={`p-6 rounded-3xl border shadow-sm transition-all ${activeAlerts > 0 ? 'bg-red-50 border-red-100 animate-pulse-slow' : 'bg-white border-gray-100'}`}>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Alertas Pendentes</p>
            <p className={`text-4xl font-black ${activeAlerts > 0 ? 'text-red-600' : 'text-gray-800'}`}>{activeAlerts}</p>
          </div>
        </div>

        <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2 text-xl">
            <Users className="w-6 h-6 text-purple-600" />
            Minhas Pacientes
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-50">
                  <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Paciente</th>
                  <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Status</th>
                  <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Fase do Ciclo</th>
                  <th className="pb-4 font-bold text-gray-400 text-xs uppercase tracking-wider">Próxima Consulta</th>
                  <th className="pb-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {patients.map(p => (
                  <tr key={p.id} className="group hover:bg-gray-50/50 transition-colors">
                    <td className="py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-sm shadow-inner">
                          {p.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-bold text-gray-800">{p.name}</p>
                          <p className="text-xs text-gray-500">{p.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-5">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tighter ${
                        p.status === 'Em tratamento' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="py-5">
                      <p className="text-sm font-medium text-gray-600">{p.cycle}</p>
                    </td>
                    <td className="py-5">
                      <p className="text-sm font-medium text-gray-600">{p.next_appointment}</p>
                    </td>
                    <td className="py-5 text-right">
                      <button 
                        onClick={() => {setSelectedPatient(p); setCurrentView('patients');}}
                        className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
        <p className="text-gray-500 font-medium">Sincronizando dados...</p>
      </div>
    );

    if (currentView === 'patients' && selectedPatient) {
      return (
        <div className="space-y-6">
          <button 
            onClick={() => {setSelectedPatient(null); setCurrentView('dashboard');}}
            className="flex items-center gap-2 text-purple-600 font-bold text-sm hover:gap-3 transition-all"
          >
            ← Voltar para listagem
          </button>
          
          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-lg">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl flex items-center justify-center text-purple-700 font-bold text-2xl shadow-inner">
                  {selectedPatient.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-3xl font-black text-gray-800">{selectedPatient.name}</h2>
                  <p className="text-gray-500 font-medium">{selectedPatient.cycle} • {selectedPatient.status}</p>
                </div>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <button className="flex-1 md:flex-none px-6 py-3 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 transition-all shadow-lg shadow-purple-200">
                  Novo Protocolo
                </button>
                <button className="p-3 bg-white border border-gray-200 text-gray-400 rounded-xl hover:text-purple-600 transition-all">
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5 text-purple-600" />
                  Dados do Ciclo
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-purple-50 p-4 rounded-2xl">
                    <p className="text-[10px] text-purple-400 font-black uppercase tracking-widest mb-1">Dia</p>
                    <p className="text-xl font-black text-purple-700">{selectedPatient.cycleData?.day || '0'}</p>
                  </div>
                  <div className="bg-pink-50 p-4 rounded-2xl">
                    <p className="text-[10px] text-pink-400 font-black uppercase tracking-widest mb-1">Folículos</p>
                    <p className="text-xl font-black text-pink-700">{selectedPatient.cycleData?.follicles || '0'}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-2xl">
                    <p className="text-[10px] text-blue-400 font-black uppercase tracking-widest mb-1">Estradiol</p>
                    <p className="text-sm font-black text-blue-700">{selectedPatient.cycleData?.estradiol || '-'}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-2xl">
                    <p className="text-[10px] text-green-400 font-black uppercase tracking-widest mb-1">Endométrio</p>
                    <p className="text-sm font-black text-green-700">{selectedPatient.cycleData?.endometrium || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-6 flex items-center gap-2 text-lg">
                  <Pill className="w-5 h-5 text-purple-600" />
                  Plano Medicamentoso
                </h3>
                <div className="space-y-4">
                  {selectedPatient.medications?.map((med: any) => (
                    <div key={med.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-transparent">
                      <div className="flex items-center gap-4">
                        <div className={`w-2 h-10 rounded-full ${med.taken ? 'bg-green-400' : 'bg-orange-400'}`}></div>
                        <div>
                          <p className="font-bold text-gray-800">{med.name}</p>
                          <p className="text-xs text-gray-500 font-medium">{med.dosage} • {med.time}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-black uppercase text-gray-300 tracking-widest mb-1">Status</p>
                        <p className={`text-xs font-black ${med.taken ? 'text-green-600' : 'text-orange-600'}`}>
                          {med.taken ? 'TOMADA' : 'PENDENTE'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-red-500" />
                  Alertas Ativos
                </h3>
                <div className="space-y-3">
                  {selectedPatient.alerts?.map((alert: any) => (
                    <div key={alert.id} className="p-4 bg-red-50 rounded-2xl border border-red-100 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-bold text-red-800">{alert.message}</p>
                        <p className="text-[10px] text-red-400 mt-1">{alert.time}</p>
                      </div>
                    </div>
                  ))}
                  {(!selectedPatient.alerts || selectedPatient.alerts.length === 0) && (
                    <div className="text-center py-6">
                      <CheckCircle className="w-10 h-10 text-green-200 mx-auto mb-2" />
                      <p className="text-xs text-gray-400 font-medium">Sem alertas no momento</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-3xl border border-purple-100 shadow-sm">
                <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Próxima Consulta
                </h3>
                <p className="text-lg font-black text-gray-800 mb-4">{selectedPatient.next_appointment}</p>
                <button className="w-full py-3 bg-white border border-purple-200 text-purple-600 rounded-xl font-bold text-xs hover:bg-purple-600 hover:text-white transition-all shadow-sm">
                  Reagendar
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (user?.role === 'patient') return renderPatientDashboard();
    if (user?.role === 'doctor' || user?.role === 'secretary') return renderDoctorDashboard();
    
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sidebar for Desktop */}
      <div className="flex-1 flex overflow-hidden">
        <aside className={`${menuOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-100 transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto`}>
          <div className="h-full flex flex-col p-6">
            <div className="flex items-center gap-3 mb-12">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                <Heart className="w-6 h-6" />
              </div>
              <h1 className="text-xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                FertilCare
              </h1>
              <button onClick={() => setMenuOpen(false)} className="lg:hidden ml-auto">
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <nav className="flex-1 space-y-2">
              {getNavigation().map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setCurrentView(item.id);
                      setMenuOpen(false);
                      if (item.id !== 'patients') setSelectedPatient(null);
                    }}
                    className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-sm transition-all ${
                      currentView === item.id
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-100 scale-[1.02]'
                        : 'text-gray-400 hover:text-purple-600 hover:bg-purple-50'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            <div className="mt-auto pt-6 border-t border-gray-100">
              <div className="bg-gray-50 p-4 rounded-2xl flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-purple-600 font-bold shadow-sm">
                  {user?.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-black text-gray-800 truncate">{user?.name}</p>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">{user?.role}</p>
                </div>
              </div>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold text-sm text-red-400 hover:bg-red-50 hover:text-red-600 transition-all"
              >
                <LogOut className="w-5 h-5" />
                Sair
              </button>
            </div>
          </div>
        </aside>

        {/* Main Area */}
        <main className="flex-1 flex flex-col min-w-0 bg-gray-50/50 overflow-y-auto">
          {/* Mobile Header */}
          <header className="lg:hidden bg-white border-b border-gray-100 p-4 sticky top-0 z-40">
            <div className="flex items-center justify-between">
              <button onClick={() => setMenuOpen(true)}>
                <Menu className="w-6 h-6 text-gray-600" />
              </button>
              <h2 className="text-lg font-black text-purple-600">FertilCare</h2>
              <button className="relative p-2 text-gray-400">
                <Bell className="w-6 h-6" />
                <span className="absolute top-2 right-2 w-2 h-2 bg-pink-500 rounded-full border-2 border-white"></span>
              </button>
            </div>
          </header>

          <div className="max-w-6xl mx-auto w-full p-6 lg:p-12">
            {/* Desktop Header */}
            <div className="hidden lg:flex items-center justify-between mb-12">
              <div>
                <h2 className="text-4xl font-black text-gray-800">
                  {getNavigation().find(n => n.id === currentView)?.label || 'Bem-vindo'}
                </h2>
                <p className="text-gray-400 font-medium mt-1">Plataforma de Monitoramento FertilCare</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="relative">
                  <Bell className="w-6 h-6 text-gray-300 hover:text-purple-600 transition-colors cursor-pointer" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-pink-500 rounded-full border-2 border-white"></span>
                </div>
                <div className="h-8 w-px bg-gray-200"></div>
                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}</p>
              </div>
            </div>

            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;
